import { useMutation } from '@apollo/client';
import {
  DOLink,
  SpinnerButton,
  Text,
  TextInput,
  LoadingState,
  style
} from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { LOGIN_REDIRECT_URL } from '../constants';
import copy from '../copy';
import { sendSms2fa, sendSms2faVariables } from '../generated-types/sendSms2fa';
import { LoginStatus, Type2fa } from '../interfaces/login';
import { SEND_SMS, TWO_FACTOR } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import {
  validationCopy,
  yupVerificationCharValidationFn,
  yupVerificationNumberValidation
} from '../utils/validation-utils';
import { ClickLink, ErrorBanner } from './Common';
import NewClickLink from './newBrandDesign/ClickLink';
import NewDOLink from './newBrandDesign/DoLink';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewText from './newBrandDesign/Text';
import NewTextInput from './newBrandDesign/TextInput';
import NewTitle from './newBrandDesign/Title';

const Title = styled.h4``;

const Spinner = styled(LoadingState)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin: 0 ${style.vars.space['1']};
`;

const copy2fa = copy.form.two_factor_auth;

const VALIDATION_2FA = {
  authenticator: Yup.object().shape({
    code: yupVerificationNumberValidation
  }),
  sms: Yup.object().shape({
    code: yupVerificationCharValidationFn()
  }),
  recovery_codes: Yup.object().shape({
    code: yupVerificationCharValidationFn(true)
  })
};

const TwoFactorSmsHeader: React.FC<{ isNewBrandDesignEnabled: boolean }> = (
  isNewBrandDesignEnabled
) => {
  const [sendSms, { loading: isSendingSMS }] = useMutation<
    sendSms2fa,
    sendSms2faVariables
  >(SEND_SMS);

  const ClickLinkComponent = isNewBrandDesignEnabled ? NewClickLink : ClickLink;
  const TextComponent = isNewBrandDesignEnabled ? NewText : Text;

  return (
    <TextComponent className={isNewBrandDesignEnabled ? 'do-u-mt--medium' : ''}>
      {copy2fa.sms.details}
      <br />
      <ClickLinkComponent
        disabled={isSendingSMS}
        onClick={() =>
          sendSms({
            variables: {
              sendSms2faRequest: addSessionData()
            }
          })
        }
      >
        {copy2fa.sms.resend_link}
      </ClickLinkComponent>
      {isSendingSMS && <Spinner />}
    </TextComponent>
  );
};

interface Props {
  type: Type2fa;
  nextStepType?: Type2fa;
  changeStep: () => void;
  isSecondary?: boolean;
  isNewBrandDesignEnabled: boolean;
}

const TwoFactorFooter: React.FC<Props> = ({
  type,
  nextStepType,
  changeStep,
  isSecondary = false,
  isNewBrandDesignEnabled = false
}) => {
  const DOLinkComponent = isNewBrandDesignEnabled ? NewDOLink : DOLink;
  const ClickLinkComponent = isNewBrandDesignEnabled ? NewClickLink : ClickLink;
  const TextComponent = isNewBrandDesignEnabled ? NewText : Text;

  const [sendSms, { loading: isSendingSMS }] = useMutation<
    sendSms2fa,
    sendSms2faVariables
  >(SEND_SMS);

  const hasNextPage = isSecondary || nextStepType;
  return (
    <>
      <TextComponent
        className={
          isNewBrandDesignEnabled
            ? 'do-u-mt--medium do-u-mb--small need-help-text'
            : 'do-u-mb--remove'
        }
      >
        {copy2fa[type].no_access}
      </TextComponent>
      {hasNextPage && (
        <ClickLinkComponent
          disabled={isSendingSMS}
          onClick={() => {
            if (nextStepType === 'sms') {
              return sendSms({
                variables: {
                  sendSms2faRequest: addSessionData()
                }
              }).finally(changeStep);
            }
            changeStep();
          }}
        >
          {nextStepType &&
            copy2fa[nextStepType][isSecondary ? 'forward_link' : 'backup_link']}
        </ClickLinkComponent>
      )}
      {isSendingSMS && <Spinner />}
      {hasNextPage && <> {copy.form.or} </>}
      <DOLinkComponent path="/support/contact">
        {copy.form.contact_support_link.replace(/./, (str: string) =>
          hasNextPage ? str : str.toUpperCase()
        )}
      </DOLinkComponent>
    </>
  );
};

interface TwoFactorGqlResponse {
  twoFactor: {
    status: LoginStatus;
    session_data: string;
    redirect_url: string;
  };
}

interface TwoFactorGqlVariables {
  twoFactorRequest: {
    type: Type2fa;
    code: string;
    session_data?: string;
  };
}

const TwoFactorAuthForm: React.FC<Props> = (props) => {
  const { isNewBrandDesignEnabled } = props;
  const TitleComponent = isNewBrandDesignEnabled ? NewTitle : Title;
  const TextComponent = isNewBrandDesignEnabled ? NewText : Text;
  const TextInputComponent = isNewBrandDesignEnabled ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrandDesignEnabled
    ? NewErrorBanner
    : ErrorBanner;
  const SpinnerButtonComponent = isNewBrandDesignEnabled
    ? NewSpinnerButton
    : SpinnerButton;

  const { type, isSecondary = false } = props;
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [twoFactor, { loading: isSendingSMS }] = useMutation<
    TwoFactorGqlResponse,
    TwoFactorGqlVariables
  >(TWO_FACTOR);

  return (
    <>
      <TitleComponent>
        {copy2fa[isSecondary ? 'backup_title' : 'title']}
      </TitleComponent>
      {type === 'sms' ? (
        <TwoFactorSmsHeader isNewBrandDesignEnabled={isNewBrandDesignEnabled} />
      ) : (
        <TextComponent
          className={isNewBrandDesignEnabled ? 'do-u-mt--medium' : ''}
        >
          {copy2fa[type].details}
        </TextComponent>
      )}

      <Formik
        initialValues={{
          code: ''
        }}
        validationSchema={VALIDATION_2FA[type]}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          twoFactor({
            variables: {
              twoFactorRequest: addSessionData({ type, ...values })
            }
          })
            .then((successResponse) => {
              const {
                status,
                redirect_url: redirectURL,
                session_data: sessionData
              } = _get(successResponse, 'data.twoFactorVerification');

              saveSessionData(sessionData);

              if (status === 'granted') {
                setIsRedirecting(true);
                window.location.assign(redirectURL || LOGIN_REDIRECT_URL);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.incorrect_two_factor);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched, setFieldValue }) => (
          <Form className="do-u-pb--small">
            {genericServerError && (
              <ErrorBannerComponent error={genericServerError} />
            )}

            <TextInputComponent
              className={isNewBrandDesignEnabled ? 'do-u-mt--medium' : ''}
              name="code"
              placeholder={`${copy2fa.placeholder_prefix} ${validationCopy.two_factor_auth[type].code_label}`}
              label={validationCopy.two_factor_auth[type].code_label}
              autoComplete="one-time-code"
              touched={touched.code}
              error={errors.code}
              value={values.code}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setFieldValue('code', e.currentTarget?.value?.trim());
              }}
            />
            <SpinnerButtonComponent
              className={isNewBrandDesignEnabled ? '' : 'do-u-mt--large'}
              type="submit"
              fullWidth
              spinning={isSendingSMS || isRedirecting}
              disabled={isSendingSMS || isRedirecting}
              loadingMessage={copy.loading.login.belowButton}
            >
              {copy2fa.button_text}
            </SpinnerButtonComponent>

            <TwoFactorFooter
              {...props}
              isNewBrandDesignEnabled={isNewBrandDesignEnabled}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default TwoFactorAuthForm;
